import styled from '@emotion/styled'
import {
    faChevronDown,
    faChevronLeft,
    faChevronRight,
    faChevronUp,
    faSearch as searchIcon,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { parseDate } from '@internationalized/date'
import {
    Box,
    Checkbox,
    Collapse,
    FormControlLabel,
    IconButton,
    InputAdornment,
    MenuItem,
    Paper,
    Select,
    TextField,
    Typography,
} from '@mui/material'
import React, { useEffect, useState } from 'react'
import {
    RangeCalendar as AriaRangeCalendar,
    CalendarCell,
    CalendarGrid,
    CalendarGridBody,
    CalendarHeading,
} from 'react-aria-components/RangeCalendar'
import { useTranslation } from 'react-i18next'

export type FilterConfig = {
    search?: {
        placeholder?: string
        fields?: string[]
    }
    select?: {
        field: string
        label: string
        options: { value: string; label: string }[]
        multiple?: boolean
    }[]
    checkbox?: {
        field: string
        label: string
    }[]
    dateRange?: {
        startDateField: string
        endDateField: string
        startLabel: string
        endLabel: string
        availableDates?: string[]
    }
    numberRange?: {
        field: string
        label: string
        min?: number
        max?: number
    }
    category?: {
        field: string
        label: string
        options: string[]
    }
    sort?: {
        options: { value: string; label: string }[]
    }
}

export type FilterProps = {
    config: FilterConfig
    onFiltersChange: (filters: Record<string, any>) => void
    initialFilters?: Record<string, any>
    resultsCount?: number
    collapsible?: boolean
    defaultCollapsed?: boolean
}

const Filter: React.FC<FilterProps> = ({
    config,
    onFiltersChange,
    initialFilters = {},
    resultsCount,
    collapsible = true,
    defaultCollapsed = true,
}) => {
    const { t } = useTranslation()
    const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed)
    const [filters, setFilters] = useState<Record<string, any>>({
        search: '',
        sort: 'default',
        ...initialFilters,
        ...(config.category && { [config.category.field]: 'all' }),
    })

    useEffect(() => {
        onFiltersChange(filters)
    }, [filters, onFiltersChange])

    const handleSearchChange = (value: string) => {
        setFilters((prev) => ({ ...prev, search: value }))
    }

    const handleCategoryChange = (category: string) => {
        setFilters((prev) => ({ ...prev, [config.category?.field || 'category']: category }))
    }

    const handleSortChange = (sort: string) => {
        setFilters((prev) => ({ ...prev, sort }))
    }

    const handleSelectChange = (field: string, value: string | string[]) => {
        setFilters((prev) => ({ ...prev, [field]: value }))
    }

    const handleCheckboxChange = (field: string, checked: boolean) => {
        setFilters((prev) => ({ ...prev, [field]: checked }))
    }

    const handleResetAll = () => {
        const resetFilters: Record<string, any> = {
            search: '',
            sort: 'default',
            ...initialFilters,
            ...(config.category && { [config.category.field]: 'all' }),
        }

        // Reset select fields
        if (config.select) {
            config.select.forEach((selectConfig) => {
                resetFilters[selectConfig.field] = selectConfig.multiple ? [] : 'all'
            })
        }

        // Reset checkbox fields
        if (config.checkbox) {
            config.checkbox.forEach((checkboxConfig) => {
                resetFilters[checkboxConfig.field] = false
            })
        }

        // Reset dateRange fields
        if (config.dateRange) {
            resetFilters[config.dateRange.startDateField] = null
            resetFilters[config.dateRange.endDateField] = null
        }

        // Reset numberRange field
        if (config.numberRange) {
            resetFilters[config.numberRange.field] = undefined
        }

        setFilters(resetFilters)
    }

    return (
        <Paper
            sx={{
                mb: 4,
                borderRadius: 3,
                boxShadow: 3,
                backgroundColor: 'white',
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    p: 3,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: collapsible ? 'pointer' : 'default',
                }}
                onClick={() => collapsible && setIsCollapsed(!isCollapsed)}
            >
                <Typography sx={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{t('common.filters')}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {resultsCount !== undefined && (
                        <Typography sx={{ fontSize: '1.2rem' }}>
                            {t('filter.resultsCount', { count: resultsCount })}
                        </Typography>
                    )}
                    <Typography
                        sx={{
                            fontSize: '1.2rem',
                            color: '#B40000',
                            cursor: 'pointer',
                            fontWeight: 'bold',
                            padding: '8px 16px',
                            borderRadius: '8px',
                            border: '2px solid #B40000',
                            backgroundColor: 'white',
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                                backgroundColor: '#B40000',
                                color: 'white',
                                transform: 'translateY(-1px)',
                            },
                            '&:active': {
                                transform: 'translateY(0)',
                            },
                        }}
                        onClick={(e) => {
                            e.stopPropagation()
                            handleResetAll()
                        }}
                    >
                        {t('filter.resetAll')}
                    </Typography>
                    {collapsible && (
                        <IconButton
                            onClick={(e) => {
                                e.stopPropagation()
                                setIsCollapsed(!isCollapsed)
                            }}
                            sx={{ color: '#B40000' }}
                        >
                            <FontAwesomeIcon icon={isCollapsed ? faChevronDown : faChevronUp} />
                        </IconButton>
                    )}
                </Box>
            </Box>

            {/* Content */}
            <Collapse in={!isCollapsed}>
                <Box sx={{ px: 3, pb: 3 }}>
                    {/* Search */}
                    {config.search && (
                        <Box sx={{ mb: 3 }}>
                            <TextField
                                fullWidth
                                label={config.search.placeholder || t('filter.search.placeholder')}
                                variant="outlined"
                                value={filters.search}
                                onChange={(e) => handleSearchChange(e.target.value)}
                                slotProps={{
                                    inputLabel: { sx: { fontSize: '1.5rem' } },
                                    htmlInput: { sx: { fontSize: '1.2rem' } },
                                    input: {
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <FontAwesomeIcon icon={searchIcon} size="sm" />
                                            </InputAdornment>
                                        ),
                                    },
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '&:hover fieldset': {
                                            borderColor: '#B40000',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#B40000',
                                        },
                                    },
                                }}
                            />
                        </Box>
                    )}

                    {/* Category Badges */}
                    {(() => {
                        if (!config.category) return null
                        const categoryConfig = config.category
                        const categoryField = categoryConfig.field
                        return (
                            <Box sx={{ mb: 3 }}>
                                <Typography sx={{ fontSize: '1.5rem', fontWeight: 'bold', mb: 2 }}>
                                    {categoryConfig.label || t('filter.category.label')}:
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                    <CategoryBadge
                                        selected={filters[categoryField] === 'all'}
                                        onClick={() => handleCategoryChange('all')}
                                    >
                                        {t('filter.category.all')}
                                    </CategoryBadge>
                                    {categoryConfig.options.map((option) => (
                                        <CategoryBadge
                                            key={option}
                                            selected={filters[categoryField] === option}
                                            onClick={() => handleCategoryChange(option)}
                                        >
                                            {option}
                                        </CategoryBadge>
                                    ))}
                                </Box>
                            </Box>
                        )
                    })()}

                    {/* Select Filters */}
                    {config.select &&
                        config.select.map((selectConfig) => (
                            <Box key={selectConfig.field} sx={{ mb: 3 }}>
                                <Typography sx={{ fontSize: '1.5rem', fontWeight: 'bold', mb: 2 }}>
                                    {selectConfig.label}:
                                </Typography>
                                <Select
                                    fullWidth
                                    value={filters[selectConfig.field] || 'all'}
                                    onChange={(e) => handleSelectChange(selectConfig.field, e.target.value)}
                                    data-testid={`${selectConfig.field}-filter`}
                                    sx={{
                                        minWidth: 200,
                                        fontSize: '1.2rem',
                                        '& .MuiOutlinedInput-root': {
                                            '&:hover fieldset': {
                                                borderColor: '#B40000',
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: '#B40000',
                                            },
                                        },
                                    }}
                                >
                                    <MenuItem
                                        value="all"
                                        sx={{ fontSize: '1.2rem' }}
                                        data-testid={`${selectConfig.field}-all`}
                                    >
                                        <em>{t('common.all')}</em>
                                    </MenuItem>
                                    {selectConfig.options.map((option) => (
                                        <MenuItem
                                            key={option.value}
                                            value={option.value}
                                            sx={{ fontSize: '1.2rem' }}
                                            data-testid={`${selectConfig.field}-${option.value}`}
                                        >
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Box>
                        ))}

                    {/* Checkbox Filters */}
                    {config.checkbox &&
                        config.checkbox.map((checkboxConfig) => (
                            <Box key={checkboxConfig.field} sx={{ mb: 3 }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={filters[checkboxConfig.field] || false}
                                            onChange={(e) =>
                                                handleCheckboxChange(checkboxConfig.field, e.target.checked)
                                            }
                                            sx={{
                                                color: '#B40000',
                                                '&.Mui-checked': {
                                                    color: '#B40000',
                                                },
                                            }}
                                        />
                                    }
                                    label={
                                        <Typography sx={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
                                            {checkboxConfig.label}
                                        </Typography>
                                    }
                                />
                            </Box>
                        ))}

                    {/* Date Range */}
                    {config.dateRange && (
                        <Box sx={{ mb: 3 }}>
                            <Typography sx={{ fontSize: '1.5rem', fontWeight: 'bold', mb: 2 }}>
                                {config.dateRange.startLabel}:
                            </Typography>
                            <CalendarWrapper>
                                <AriaRangeCalendar
                                    value={(() => {
                                        const startDate = filters[config.dateRange!.startDateField]
                                        const endDate = filters[config.dateRange!.endDateField]
                                        const formatDateToLocal = (isoString: string) => {
                                            const date = new Date(isoString)
                                            const year = date.getFullYear()
                                            const month = String(date.getMonth() + 1).padStart(2, '0')
                                            const day = String(date.getDate()).padStart(2, '0')
                                            return `${year}-${month}-${day}`
                                        }
                                        return startDate && endDate
                                            ? {
                                                  start: parseDate(formatDateToLocal(startDate)),
                                                  end: parseDate(formatDateToLocal(endDate)),
                                              }
                                            : null
                                    })()}
                                    isDateUnavailable={(date) => {
                                        // Check if date should be disabled based on available dates
                                        return config.dateRange?.availableDates
                                            ? !config.dateRange.availableDates.includes(date.toString())
                                            : false
                                    }}
                                    onChange={(newValue: any) => {
                                        setFilters((prev) => {
                                            const formatDateToLocalISO = (date: Date) => {
                                                const year = date.getFullYear()
                                                const month = String(date.getMonth() + 1).padStart(2, '0')
                                                const day = String(date.getDate()).padStart(2, '0')
                                                return `${year}-${month}-${day}T00:00:00.000Z`
                                            }
                                            const updated = {
                                                ...prev,
                                                [config.dateRange!.startDateField]: newValue?.start
                                                    ? formatDateToLocalISO(newValue.start.toDate())
                                                    : null,
                                                [config.dateRange!.endDateField]: newValue?.end
                                                    ? formatDateToLocalISO(newValue.end.toDate())
                                                    : null,
                                            }
                                            return updated
                                        })
                                    }}
                                >
                                    <CalendarHeader>
                                        <NavButton slot="previous">
                                            <FontAwesomeIcon icon={faChevronLeft} />
                                        </NavButton>
                                        <CalendarHeading />
                                        <NavButton slot="next">
                                            <FontAwesomeIcon icon={faChevronRight} />
                                        </NavButton>
                                    </CalendarHeader>
                                    <CalendarGrid>
                                        <CalendarGridBody>
                                            {(date) => (
                                                <CalendarCell date={date}>
                                                    {({
                                                        formattedDate,
                                                        isSelected,
                                                        isSelectionStart,
                                                        isSelectionEnd,
                                                        isDisabled,
                                                    }) => (
                                                        <CalendarDay
                                                            isSelected={isSelected}
                                                            isSelectionStart={isSelectionStart}
                                                            isSelectionEnd={isSelectionEnd}
                                                            isDisabled={isDisabled}
                                                        >
                                                            {formattedDate}
                                                        </CalendarDay>
                                                    )}
                                                </CalendarCell>
                                            )}
                                        </CalendarGridBody>
                                    </CalendarGrid>
                                </AriaRangeCalendar>
                            </CalendarWrapper>
                        </Box>
                    )}

                    {/* Sort */}
                    {config.sort && (
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                            <Typography sx={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
                                {t('filter.sort.label')}:
                            </Typography>
                            <Select
                                value={filters.sort || 'default'}
                                onChange={(e) => handleSortChange(e.target.value)}
                                sx={{
                                    minWidth: 200,
                                    fontSize: '1.2rem',
                                    '& .MuiOutlinedInput-root': {
                                        '&:hover fieldset': {
                                            borderColor: '#B40000',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#B40000',
                                        },
                                    },
                                }}
                            >
                                <MenuItem value="default" sx={{ fontSize: '1.2rem' }}>
                                    {t('filter.sort.default')}
                                </MenuItem>
                                {config.sort.options.map((option) => (
                                    <MenuItem key={option.value} value={option.value} sx={{ fontSize: '1.2rem' }}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </Box>
                    )}
                </Box>
            </Collapse>
        </Paper>
    )
}

const CategoryBadge = styled.button<{ selected: boolean }>`
    user-select: none;
    appearance: none;
    border: 2px solid #b40000;
    background-color: ${(props) => (props.selected ? '#B40000' : 'transparent')};
    color: ${(props) => (props.selected ? '#ffffff' : '#B40000')};
    padding: 8px 16px;
    border-radius: 24px;
    font-size: 1.2rem;
    font-weight: bold;
    cursor: pointer;
    transition: all 0.2s ease-in-out;

    &:hover {
        opacity: 0.8;
        transform: translateY(-2px);
    }

    &:active {
        transform: translateY(0);
    }
`

const CalendarWrapper = styled.div`
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    padding: 16px;
    background: white;
    font-size: 1.2rem;
    max-width: fit-content;
`

const CalendarHeader = styled.header`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
    padding: 0 8px;
`

const NavButton = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    padding: 8px;
    border-radius: 4px;
    color: #b40000;
    font-size: 1rem;
    transition: background-color 0.2s;

    &:hover {
        background-color: rgba(180, 0, 0, 0.1);
    }
`

const CalendarDay = styled.span<{
    isSelected?: boolean
    isSelectionStart?: boolean
    isSelectionEnd?: boolean
    isDisabled?: boolean
}>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    padding: 8px;
    border-radius: 50%;
    cursor: pointer;
    transition: background-color 0.2s;
    font-size: 1rem;

    &:hover {
        background-color: rgba(180, 0, 0, 0.1);
    }

    ${(props) =>
        props.isDisabled &&
        `
        cursor: not-allowed;
        opacity: 0.3;
        color: #ccc;
        
        &:hover {
            background-color: transparent;
        }
    `}

    ${(props) =>
        props.isSelected &&
        (props.isSelectionStart || props.isSelectionEnd) &&
        !props.isDisabled &&
        `
        background-color: #B40000;
        color: white;
    `}

    ${(props) =>
        props.isSelected &&
        !props.isSelectionStart &&
        !props.isSelectionEnd &&
        !props.isDisabled &&
        `
        background-color: rgba(180, 0, 0, 0.3);
        color: #B40000;
    `}
`

export default Filter
